// src/components/professor/IntegrationSection.js

import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import Preloader from "../../components/Preloader";
import { onAuthStateChanged } from "firebase/auth";

function IntegrationSection() {
  const FUNCTIONS_BASE = process.env.REACT_APP_FIREBASE_FUNCTIONS;
  const [activeTab, setActiveTab] = useState("grade");
  const [selectedService, setSelectedService] = useState("");
  const [assignmentUrl, setAssignmentUrl] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [errors, setErrors] = useState({});
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [classes, setClasses] = useState([]);
  const [user, setUser] = useState(null);

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Fetch classes for the current user
  useEffect(() => {
    const fetchClasses = async () => {
      if (!user || classes.length > 0) return; // Prevent re-fetching

      setLoading(true);
      try {
        const qClasses = query(
          collection(db, "classes"),
          where("professor_uid", "==", user.uid)
        );
        const querySnapshot = await getDocs(qClasses);
        const classesData = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setClasses(classesData);
      } catch (error) {
        console.error("Error fetching classes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, [user, classes.length]);

  const handleServiceChange = (e) => {
    setSelectedService(e.target.value);
    setAssignmentUrl("");
    setApiToken("");
    setErrors({});
  };

  // Simple URL validation for Canvas assignment
  const validateAssignmentUrl = (url) => {
    const pattern = /^https?:\/\/[^/]+\/courses\/\d+\/assignments\/\d+$/;
    return pattern.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let currentErrors = {};

    // Validate class selection
    if (!selectedClassId) {
      currentErrors.selectedClassId = "Please select a class.";
    }

    // Validate fields if Canvas is selected
    if (selectedService === "canvas") {
      if (!assignmentUrl) {
        currentErrors.assignmentUrl = "Assignment URL is required.";
      } else if (!validateAssignmentUrl(assignmentUrl)) {
        currentErrors.assignmentUrl = "Assignment URL format is invalid.";
      }
      if (!apiToken) {
        currentErrors.apiToken = "API Token is required.";
      }
    }

    if (Object.keys(currentErrors).length > 0) {
      setErrors(currentErrors);
      return;
    }

    if (!user) {
      alert("You must be signed in to perform this action.");
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE}/pushGradesToCanvas`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assignmentUrl,
            selectedClassId,
            apiToken,
            professorUid: user.uid,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const { reportData } = await response.json();
      setReportData(reportData);
      alert("Grade push completed.");
    } catch (error) {
      console.error("Error pushing grades:", error);
      alert(error.message || "Failed to push grades.");
    } finally {
      setLoading(false);
    }
  };

  // Generate CSV content for downloaded report
  const generateCsv = (report) => {
    const csvRows = [];
    csvRows.push(["Student", "Grade", "Status", "Message"]);
    for (const row of report) {
      // Wrap message in quotes if needed
      csvRows.push([row.student, row.grade, row.status, `"${row.message}"`]);
    }
    return csvRows.map((e) => e.join(",")).join("\n");
  };

  const downloadCsv = () => {
    const csvContent = generateCsv(reportData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "grade_push_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section
      id="integration-section"
      className="flex flex-col items-center justify-start min-h-screen p-8"
      style={{ backgroundColor: "var(--section-bg)", color: "var(--text-color)" }}
    >
      {loading && <Preloader />}
  
      {/* Tabs (though only one tab here, we keep the structure for consistency) */}
      <ul
        className="flex w-full max-w-3xl mb-4"
        style={{ borderBottom: "1px solid var(--muted-color)" }}
      >
        <li>
          <button
            className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
              activeTab === "grade"
                ? "border-[var(--primary-color)] text-[var(--primary-color)]"
                : "border-transparent hover:border-[var(--hover-color)] text-[var(--muted-color)]"
            }`}
            onClick={() => setActiveTab("grade")}
          >
            Grade
          </button>
        </li>
      </ul>
  
      <div className="w-full max-w-3xl">
        {activeTab === "grade" && (
          <div className="flex flex-col space-y-6">
            <p>Select a service to integrate with your grading system.</p>
  
            {/* Class Selection */}
            <div className="flex flex-col md:flex-row md:items-center mb-2">
              <label
                htmlFor="classSelect"
                className="md:w-1/3 md:text-right md:pr-4 mb-1 md:mb-0"
              >
                Select Class:
              </label>
              <div className="md:w-2/3">
                <select
                  id="classSelect"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
                  required
                >
                  <option value="">Select a class</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
                {errors.selectedClassId && (
                  <div className="text-red-500 mt-1 text-sm">
                    {errors.selectedClassId}
                  </div>
                )}
              </div>
            </div>
  
            {/* Service Selection */}
            <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
              <div className="flex flex-col md:flex-row md:items-center">
                <label
                  htmlFor="serviceSelect"
                  className="md:w-1/3 md:text-right md:pr-4 mb-1 md:mb-0"
                >
                  Select Service:
                </label>
                <div className="md:w-2/3">
                  <select
                    id="serviceSelect"
                    value={selectedService}
                    onChange={handleServiceChange}
                    className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="canvas">Canvas</option>
                  </select>
                </div>
              </div>
  
              {/* Canvas Fields */}
              {selectedService === "canvas" && (
                <>
                  <div className="flex flex-col">
                    <input
                      type="text"
                      placeholder="Assignment URL"
                      value={assignmentUrl}
                      onChange={(e) => setAssignmentUrl(e.target.value)}
                      className="p-2 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                      required
                    />
                    {errors.assignmentUrl && (
                      <div className="text-red-500 mt-1 text-sm">
                        {errors.assignmentUrl}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <input
                      type="password"
                      placeholder="API Token"
                      value={apiToken}
                      onChange={(e) => setApiToken(e.target.value)}
                      className="p-2 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                      required
                    />
                    {errors.apiToken && (
                      <div className="text-red-500 mt-1 text-sm">
                        {errors.apiToken}
                      </div>
                    )}
                  </div>
                </>
              )}
  
              <button
                type="submit"
                className="self-start px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded"
              >
                Push Grades
              </button>
            </form>
  
            {/* Download CSV if data is available */}
            {reportData.length > 0 && (
              <button
                onClick={downloadCsv}
                className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--hover-color)] text-white rounded self-start"
              >
                Download CSV
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
  
}

export default IntegrationSection;
